import { z } from 'zod';
// ---------- A3 Comunicación omnicanal ----------
export const CommunicationChannelKindSchema = z.enum([
    'EMAIL',
    'PHONE',
    'MEETING',
    'WHATSAPP',
    'LETTER',
    'VISIT',
    'OTHER',
]);
export const CommunicationChannelLabel = {
    EMAIL: 'Correo',
    PHONE: 'Llamada',
    MEETING: 'Reunión',
    WHATSAPP: 'WhatsApp',
    LETTER: 'Carta / burofax',
    VISIT: 'Visita',
    OTHER: 'Otro',
};
export const CommunicationDirectionSchema = z.enum(['INBOUND', 'OUTBOUND', 'BOTH']);
export const CommunicationDirectionLabel = {
    INBOUND: 'Entrante',
    OUTBOUND: 'Saliente',
    BOTH: 'En ambos sentidos',
};
export const CommunicationStatusSchema = z.enum(['ASIGNADA', 'SIN_ASIGNAR', 'DESCARTADA']);
export const CommunicationSourceSchema = z.enum(['EMAIL', 'MANUAL', 'SYSTEM']);
export const CommunicationAssignModeSchema = z.enum(['AUTO', 'RULE', 'MANUAL']);
export const CommunicationAssignModeLabel = {
    AUTO: 'Asignada automáticamente',
    RULE: 'Asignada por regla',
    MANUAL: 'Asignada a mano',
};
export const CommunicationDocumentDtoSchema = z.object({
    id: z.string(),
    fileName: z.string(),
});
export const CommunicationDtoSchema = z.object({
    id: z.string(),
    channel: CommunicationChannelKindSchema,
    direction: CommunicationDirectionSchema,
    source: CommunicationSourceSchema,
    status: CommunicationStatusSchema,
    assignMode: CommunicationAssignModeSchema.nullable(),
    clientId: z.string().nullable(),
    clientName: z.string().nullable(),
    caseId: z.string().nullable(),
    caseCode: z.string().nullable(),
    occurredAt: z.string(),
    subject: z.string().nullable(),
    summary: z.string(),
    counterpartName: z.string().nullable(),
    counterpartEmail: z.string().nullable(),
    counterpartPhone: z.string().nullable(),
    /** Correo sincronizado del que procede (solo lo abre su propietario en el Buzón). */
    emailMessageId: z.string().nullable(),
    /** El usuario actual es el dueño del buzón o quien lo registró. */
    isMine: z.boolean(),
    /** El usuario actual puede editarlo o borrarlo (solo los registrados a mano). */
    canEdit: z.boolean(),
    createdByName: z.string().nullable(),
    documents: z.array(CommunicationDocumentDtoSchema),
});
export const CommunicationListQuerySchema = z.object({
    channel: CommunicationChannelKindSchema.optional(),
    direction: CommunicationDirectionSchema.optional(),
    caseId: z.string().optional(),
    q: z.string().trim().max(200).optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(25),
});
export const CommunicationListResponseSchema = z.object({
    items: z.array(CommunicationDtoSchema),
    total: z.number().int(),
    page: z.number().int(),
    pageSize: z.number().int(),
    /** Cuántas hay por canal (sin aplicar el filtro de canal), para los filtros. */
    channelCounts: z.record(z.string(), z.number().int()),
});
const optionalText = (max) => z.string().trim().max(max).nullable().optional();
export const CreateCommunicationSchema = z
    .object({
    channel: CommunicationChannelKindSchema,
    direction: CommunicationDirectionSchema,
    occurredAt: z.string().datetime(),
    subject: optionalText(300),
    summary: z.string().trim().max(5000).default(''),
    /** Sin cliente queda «Sin asignar» (p. ej. una llamada de un número desconocido). */
    clientId: z.string().nullable().optional(),
    caseId: z.string().nullable().optional(),
    counterpartName: optionalText(200),
    counterpartEmail: z.string().trim().email().max(200).nullable().optional(),
    counterpartPhone: optionalText(40),
})
    .superRefine((d, ctx) => {
    if (!d.subject?.trim() && !d.summary.trim()) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['summary'],
            message: 'Escribe un asunto o un resumen',
        });
    }
    if (new Date(d.occurredAt).getTime() > Date.now() + 10 * 60_000) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['occurredAt'],
            message: 'La fecha no puede estar en el futuro',
        });
    }
    if (d.caseId && !d.clientId) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['clientId'],
            message: 'Indica el cliente del expediente',
        });
    }
});
export const UpdateCommunicationSchema = z
    .object({
    channel: CommunicationChannelKindSchema.optional(),
    direction: CommunicationDirectionSchema.optional(),
    occurredAt: z.string().datetime().optional(),
    subject: optionalText(300),
    summary: z.string().trim().max(5000).optional(),
    caseId: z.string().nullable().optional(),
    counterpartName: optionalText(200),
    counterpartEmail: z.string().trim().email().max(200).nullable().optional(),
    counterpartPhone: optionalText(40),
})
    .refine((d) => Object.keys(d).length > 0, { message: 'Sin cambios' });
// ---------- Bandeja «Sin asignar» ----------
export const ClientProposalSchema = z.object({
    clientId: z.string(),
    displayName: z.string(),
    via: z.enum(['EMAIL', 'CONTACT_EMAIL', 'PHONE', 'DOMAIN']),
    /** Por qué se propone («Email del cliente», «Contacto: Laura Gil»…). */
    label: z.string(),
    confidence: z.enum(['HIGH', 'MEDIUM', 'LOW']),
});
export const UnassignedItemSchema = CommunicationDtoSchema.extend({
    proposals: z.array(ClientProposalSchema),
});
export const UnassignedQuerySchema = z.object({
    status: z.enum(['SIN_ASIGNAR', 'DESCARTADA']).default('SIN_ASIGNAR'),
    q: z.string().trim().max(200).optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(25),
});
export const UnassignedListResponseSchema = z.object({
    items: z.array(UnassignedItemSchema),
    total: z.number().int(),
    page: z.number().int(),
    pageSize: z.number().int(),
    /** Pendientes y descartadas del usuario, para las insignias de las pestañas. */
    pendingCount: z.number().int(),
    discardedCount: z.number().int(),
});
export const AssignCommunicationSchema = z.object({
    clientId: z.string().min(1),
    caseId: z.string().nullable().optional(),
    /** Crear una regla de archivado «correos de este remitente → este cliente». */
    createRule: z.boolean().default(false),
});
export const IgnoreSenderSchema = z.object({
    email: z.string().trim().email().max(200),
});
export const LinkDocumentSchema = z.object({ documentId: z.string().min(1) });
// ---------- Canales (Ajustes) ----------
export const ChannelHealthSchema = z.object({
    /** Cuentas de correo conectadas por todo el bufete. */
    accounts: z.number().int(),
    withErrors: z.number().int(),
    lastSyncAt: z.string().nullable(),
});
export const CommunicationChannelDtoSchema = z.object({
    kind: CommunicationChannelKindSchema,
    label: z.string(),
    description: z.string(),
    enabled: z.boolean(),
    capture: z.enum(['AUTOMATIC', 'MANUAL']),
    autoAssign: z.boolean().nullable(),
    /** Solo el correo tiene salud y última sincronización. */
    health: ChannelHealthSchema.nullable(),
    /** Nº de comunicaciones asignadas por este canal. */
    total: z.number().int(),
});
export const UnavailableChannelSchema = z.object({
    id: z.string(),
    label: z.string(),
    reason: z.string(),
});
export const CommunicationChannelListResponseSchema = z.object({
    items: z.array(CommunicationChannelDtoSchema),
    /** Canales que se barajaron y NO existen todavía, con el motivo. */
    unavailable: z.array(UnavailableChannelSchema),
});
export const UpdateCommunicationChannelSchema = z
    .object({
    enabled: z.boolean().optional(),
    autoAssign: z.boolean().optional(),
})
    .refine((d) => Object.keys(d).length > 0, { message: 'Sin cambios' });
// ---------- Enviar un email al cliente desde su ficha ----------
export const singleLine = (max) => z
    .string()
    .trim()
    .min(1)
    .max(max)
    .refine((v) => !/[\r\n]/.test(v), 'No puede tener saltos de línea');
export const SendClientEmailSchema = z.object({
    /** Debe ser el email del cliente o el de uno de sus contactos. */
    to: z.string().trim().email().max(200),
    cc: z.array(z.string().trim().email().max(200)).max(5).default([]),
    subject: singleLine(200),
    body: z.string().trim().min(1).max(10_000),
    caseId: z.string().nullable().optional(),
    /** Documentos del cliente (sin expediente o del mismo expediente) que se adjuntan. */
    documentIds: z.array(z.string().min(1)).max(5).default([]),
});
export const ClientEmailOptionsSchema = z.object({
    /** El bufete tiene configurado el correo saliente (SMTP). */
    smtpConfigured: z.boolean(),
    /** Desde qué dirección sale el mensaje. */
    fromEmail: z.string().nullable(),
    /** A quién llegará la respuesta del cliente (el usuario que envía). */
    replyTo: z.string(),
    /** Destinatarios permitidos: el cliente y sus contactos con email. */
    recipients: z.array(z.object({ email: z.string(), label: z.string() })),
    /** Firma que se añade al final del mensaje. */
    signature: z.string(),
});
//# sourceMappingURL=communications.js.map